/**
 * clipProcessorDepsFromStore.ts
 *
 * Adapter that wraps IVideoCreatorStore into the narrow IClipProcessorDeps
 * interface. Existing callers (AIClipProducer, VideoCreatorStore) use this
 * to construct deps from the global store — zero behavioral change.
 *
 * See: docs/refactors/transcript-dependency-injection.md (Step 9c)
 */

import type { IVideoCreatorStore } from '../stores/IVideoCreatorStore';
import type { IClipProcessorDeps } from './IClipProcessorDeps';

/**
 * Build IClipProcessorDeps from the global IVideoCreatorStore.
 * Preserves all existing behavior by delegating to the same rootStore chains.
 */
export function clipProcessorDepsFromStore(
  videoCreator: IVideoCreatorStore,
): IClipProcessorDeps {
  return {
    prepareAsCurrentLoadedVideo: (originalVideo, title, sourcePlatform) =>
      videoCreator.prepareAsCurrentLoadedVideo(
        originalVideo,
        title,
        sourcePlatform,
      ),

    get story() {
      return videoCreator.story ?? undefined;
    },

    get currentEditor() {
      return videoCreator.rootStore.userIdentityStore.currentEditor;
    },

    getTranscriptFallback: () => ({
      final:
        videoCreator.rootStore.transcriptionStore.getFinalTranscriptionElements(),
      original:
        videoCreator.rootStore.transcriptionStore.getOriginalTranscription()
          ?.elements || [],
      forcedAlignment:
        videoCreator.rootStore.transcriptionStore.isForcedAligned,
    }),

    get transcriptLanguage() {
      return videoCreator.rootStore.transcriptionStore.rawLanguage;
    },

    getKaraokeContext: () => {
      const karaokeStore = videoCreator.rootStore.karaokeStore;
      return {
        karaokeData: karaokeStore.getKaraokeData() ?? null,
        legacyKaraokeConfigLoaded:
          karaokeStore.legacyKaraokeConfigLoaded ?? null,
        createKaraokeConfig: (config: unknown) =>
          videoCreator.rootStore.editorStore.editor.createKaraokeConfig(
            config as Parameters<
              typeof videoCreator.rootStore.editorStore.editor.createKaraokeConfig
            >[0],
          ),
      };
    },

    get videoRepository() {
      return videoCreator.datoClientStore?.videoRepository ?? undefined;
    },

    get flags() {
      return {
        enableTranscriptSnapshotResourceUpload:
          !!videoCreator.flags?.enableTranscriptSnapshotResourceUpload,
        enableTranscriptSnapshotResourceDualWrite:
          !!videoCreator.flags?.enableTranscriptSnapshotResourceDualWrite,
      };
    },

    getMigrationLog: () => {
      const log =
        videoCreator.rootStore.editorStore
          .transcriptSegmentArchitectureMigrationLog;
      return log ?? undefined;
    },

    isOriginalVideoElement: (elementState: unknown) =>
      videoCreator.isOriginalVideoElement(elementState),

    get sceneDetections() {
      return videoCreator.sceneDetections;
    },

    addMultipleVideosToStory: videos =>
      videoCreator.addMultipleVideosToStory(videos),
  };
}
